import {
  CSSProperties,
  memo,
  ReactElement,
  RefObject,
  Suspense,
  useRef
} from 'react';
import cc from 'classcat';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import type { ICustomMenuItem } from 'interfaces/generic';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useUpdateEffect } from 'newhooks/generic/useUpdateEffect';
import './index.less';

//TODO as CustomMenu is UI component, need to remove Homework logic from here @todo-ignore
//attention: this should be fixed when we have partner with homework functionality
const { Homework } = lazy(
  () =>
    import(/* webpackChunkName: "homework" */ 'newelements/Generics/homework')
);

export type TCustomMenuProps = {
  items: ICustomMenuItem[];
  setActiveItem: Function;
  activeItem: number | string;
  fullWidth?: boolean;
  inverseColors?: boolean;
  disabled?: boolean;
  count?: boolean;
  homework?: boolean;
  className?: string;
  itemStyles?: CSSProperties;
};

export const CustomMenu = memo(
  ({
    items,
    fullWidth,
    activeItem,
    setActiveItem,
    inverseColors,
    disabled,
    count,
    homework,
    className,
    itemStyles
  }: TCustomMenuProps): ReactElement => {
    const tabRef: RefObject<HTMLDivElement> = useRef(null);

    useUpdateEffect(() => {
      tabRef.current?.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
        block: 'nearest'
      });
    }, [activeItem]);

    return (
      <div
        className={cc([
          `customMenu__wrapper ${className || ''}`,
          {
            'customMenu__wrapper--mobile': isMobile(),
            'customMenu__wrapper--desktop': !isMobile(),
            'customMenu__wrapper--fullWidth': fullWidth,
            'customMenu__wrapper--inverse': inverseColors,
            'customMenu__wrapper--disabled': disabled
          }
        ])}
      >
        <div className="customMenu">
          {homework && SpringConfigs.HOMEWORK_ENABLED ? (
            <Suspense>
              <Homework />
            </Suspense>
          ) : null}

          {items.map(item => (
            <div
              ref={isMobile() && activeItem === item.key ? tabRef : null}
              key={item.key || item.value}
              className={cc([
                'customMenu__item',
                {
                  'customMenu__item--mobile': isMobile(),
                  'customMenu__item--inverse': inverseColors,
                  'customMenu__item--active': activeItem === item.key
                }
              ])}
              onClick={() => setActiveItem(item.key)}
              data-testid={item.value?.toLowerCase()}
              style={itemStyles}
            >
              {item.skeleton ? (
                <Skeleton
                  className="customMenu__skeleton"
                  title={{ width: '50px' }}
                  paragraph={false}
                />
              ) : (
                <>
                  {item.icon && (
                    <div
                      className={cc([
                        'customMenu__item__icon',
                        { 'customMenu__item__icon--inverse': inverseColors }
                      ])}
                    >
                      {item.icon}
                    </div>
                  )}
                  <span
                    className={`customMenu__item--label ${
                      homework ? 'customMenu__item--label--sports-bar' : ''
                    } ${
                      activeItem === item.key && inverseColors ? 'active' : ''
                    }`}
                  >
                    {item.value}
                  </span>
                  {count && item?.count ? (
                    <span className="customMenu__item-count">{item.count}</span>
                  ) : (
                    ''
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
);
